import { useState } from 'react';
import { Button } from '@leadsnavi/ui-kit';
import cn from 'classnames';
import type { FormErrors, ResetPasswordStepProps } from '../types';
import { PasswordInput, PasswordRules, BackToLoginLink } from '../shared';
import { usePasswordValidation, isPasswordValid } from '../hooks/usePasswordValidation';

/**
 * 重置密码步骤页面
 */
export function ResetPasswordStep({
  errors: externalErrors,
  loading = false,
  onStepChange,
  onSubmit,
}: ResetPasswordStepProps) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);

  const mismatchError =
    confirmPasswordTouched && confirmPassword && newPassword !== confirmPassword
      ? 'Password does not match'
      : undefined;
  const errors: FormErrors = {
    ...localErrors,
    ...externalErrors,
    ...(mismatchError && { confirmPassword: mismatchError }),
  };
  const passwordValidation = usePasswordValidation(newPassword);

  const handleConfirmPasswordBlur = () => {
    setConfirmPasswordTouched(true);
  };

  /**
   * 验证表单
   */
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (!isPasswordValid(passwordValidation)) {
      newErrors.newPassword = 'Password does not meet requirements';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Password does not match';
    }

    setLocalErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * 提交表单
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit?.(newPassword, confirmPassword);
    }
  };

  const canSubmit =
    isPasswordValid(passwordValidation) && newPassword === confirmPassword && confirmPassword.length > 0;

  return (
    <form onSubmit={handleSubmit} className="flex w-full flex-col gap-[32px]">
      {/* 标题 */}
      <div className="flex flex-col items-start">
        <h2 className="text-[24px] font-semibold leading-[1.2] tracking-[-0.48px] text-[#09090b]">
          Reset your password
        </h2>
      </div>

      {/* 表单内容 */}
      <div className="flex flex-col gap-[24px]">
        {/* 新密码 */}
        <div className="flex flex-col gap-[12px]">
          <PasswordInput
            label="New Password"
            value={newPassword}
            onChange={setNewPassword}
            error={errors.newPassword}
            disabled={loading}
          />
          <PasswordRules validation={passwordValidation} />
        </div>

        {/* 确认密码 */}
        <PasswordInput
          label="Confirm Password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          onBlur={handleConfirmPasswordBlur}
          error={errors.confirmPassword}
          disabled={loading}
        />

        {/* 确认按钮 */}
        <Button
          type="submit"
          disabled={loading || !canSubmit}
          loading={loading}
          className={cn(
            'h-[40px] w-full rounded-[8px] bg-[#171717] px-[24px] py-[9.5px]',
            'text-[14px] font-medium leading-[1.5] tracking-[0.07px] text-white',
            'hover:bg-[#262626]',
            'disabled:cursor-not-allowed disabled:opacity-50'
          )}
        >
          Confirm
        </Button>
      </div>

      {/* 返回登录 */}
      <div className="flex justify-center">
        <BackToLoginLink onClick={() => onStepChange?.('login')} />
      </div>
    </form>
  );
}

export default ResetPasswordStep;
